"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createRewrites = createRewrites;
function createRewrites({ i18n, pageSlugs, prefixSlugs }) {
    const { locales, defaultLocale, localePrefix } = i18n;
    function validateLocale(locale) {
        if (!locales.includes(locale)) {
            throw new Error(`Invalid locale: "${locale}"`);
        }
    }
    function getPrefixSlug(locale, prefixType) {
        const prefixSlug = prefixSlugs[locale]?.[prefixType];
        if (prefixSlug) {
            return prefixSlug;
        }
        throw new Error(`Prefix type "${prefixType}" not found for locale "${locale}".`);
    }
    function getRoutePatterns(locale) {
        const electionPrefix = getPrefixSlug(locale, "election");
        return {
            // Web routes (with prefix)
            web1: { prefix: `/${electionPrefix}/:p1`, hasId: ["result"] },
            web2: { prefix: `/${electionPrefix}/:p1/:p2`, hasId: ["result"] },
            web3: { prefix: `/${electionPrefix}/:p1/:p2/:p3`, hasId: ["result"] },
            // Web routes (without the election prefix - grouped and standalone calculators)
            webDirect1: { prefix: "/:p1", hasId: ["result"] },
            webDirect2: { prefix: "/:p1/:p2", hasId: ["result"] },
            webDirect3: { prefix: "/:p1/:p2/:p3", hasId: ["result"] },
            // Embed routes
            embed1: { prefix: "/embed/:embed/:p1", hasId: [] },
            embed2: { prefix: "/embed/:embed/:p1/:p2", hasId: [] },
            embed3: { prefix: "/embed/:embed/:p1/:p2/:p3", hasId: [] },
        };
    }
    function generateSlugRewrites(fromLocale, toLocale, prefix, hasIdSlugs) {
        validateLocale(fromLocale);
        validateLocale(toLocale);
        const rewrites = [];
        const fromSlugs = pageSlugs[fromLocale];
        if (!fromSlugs) {
            throw new Error(`Locale '${fromLocale}' not found in page slugs`);
        }
        // For "as-needed" strategy, default locale doesn't have prefix in source URLs
        const isDefaultLocale = fromLocale === defaultLocale;
        const sourceLocalePrefix = localePrefix === "as-needed" && isDefaultLocale ? "" : `/${fromLocale}`;
        for (const [pageType, localizedSlug] of Object.entries(fromSlugs)) {
            const filesystemSlug = pageType;
            rewrites.push({
                source: `${sourceLocalePrefix}${prefix}/${localizedSlug}`,
                destination: `/${toLocale}${prefix}/${filesystemSlug}`,
            });
            if (pageType === "question") {
                rewrites.push({
                    source: `${sourceLocalePrefix}${prefix}/${localizedSlug}/:num`,
                    destination: `/${toLocale}${prefix}/${filesystemSlug}/:num`,
                });
            }
            if (hasIdSlugs.includes(pageType)) {
                rewrites.push({
                    source: `${sourceLocalePrefix}${prefix}/${localizedSlug}/:id`,
                    destination: `/${toLocale}${prefix}/${filesystemSlug}/:id`,
                });
            }
        }
        return rewrites;
    }
    function getSlugRewrites(fromLocale) {
        validateLocale(fromLocale);
        const toLocale = fromLocale;
        const rewrites = [];
        const routePatterns = getRoutePatterns(fromLocale);
        for (const pattern of Object.values(routePatterns)) {
            rewrites.push(...generateSlugRewrites(fromLocale, toLocale, pattern.prefix, pattern.hasId));
        }
        return rewrites;
    }
    function getLocaleRewrites() {
        validateLocale(defaultLocale);
        if (localePrefix === "as-needed") {
            const electionPrefix = getPrefixSlug(defaultLocale, "election");
            const localesPattern = locales.join("|");
            return [
                {
                    source: "/",
                    destination: `/${defaultLocale}`,
                },
                {
                    source: "/embed/:path*",
                    destination: `/${defaultLocale}/embed/:path*`,
                },
                {
                    source: `/${electionPrefix}/:path*`,
                    destination: `/${defaultLocale}/${electionPrefix}/:path*`,
                },
                {
                    source: `/:path((?!(?:${localesPattern}|api|_next|favicon\\.ico)(?:/|$)).*)*`,
                    destination: `/${defaultLocale}/:path*`,
                },
            ];
        }
        return [];
    }
    function getLocaleRedirects() {
        if (localePrefix === "as-needed") {
            return [
                {
                    source: `/${defaultLocale}/embed/:path*`,
                    destination: "/embed/:path*",
                    permanent: true,
                },
                {
                    source: `/${defaultLocale}/:path((?!embed).*)*`,
                    destination: "/:path*",
                    permanent: true,
                },
            ];
        }
        return [];
    }
    return { getSlugRewrites, getLocaleRewrites, getLocaleRedirects };
}
